import * as React from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { FieldGroup } from '@/components/ui/field'
import { StatusPill } from '@/components/ui/status-pill'
import { AreaNav } from '@/components/visitone/area-nav'
import { PageHeader } from '@/components/visitone/page-header'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import type { Role, RoleRow } from '@/lib/bff/people'
import { roleLabel } from '@/lib/people/people-text'
import { pathForDestination, SETUP_PEOPLE_CHANGES_PATH, SETUP_PEOPLE_PATH, SETUP_PEOPLE_ROLES_PATH } from '@/lib/routing'

/*
 * The frame of every People & access page: the area's header (title, the
 * page's summary and its one primary action), the area navigation filtered to
 * the pages the caller can open, and the page's own name as a visually hidden
 * heading for the content below.
 */
export function PeopleFrame({
  pageTitle,
  summary,
  action,
  rootRef,
  children,
}: {
  pageTitle: string
  summary: string
  action?: React.ReactNode
  rootRef?: React.Ref<HTMLDivElement>
  children: React.ReactNode
}) {
  const t = useT()
  const { can, canOpenSetupHome } = useAccess()
  const items = [
    ...(can('users.view')
      ? [
          { to: SETUP_PEOPLE_PATH, label: t('people.nav.users') },
          { to: SETUP_PEOPLE_ROLES_PATH, label: t('people.nav.roles') },
        ]
      : []),
    ...(can('access_changes.view') ? [{ to: SETUP_PEOPLE_CHANGES_PATH, label: t('people.nav.changes') }] : []),
  ]
  return (
    <div ref={rootRef} className="flex max-w-5xl flex-col gap-4">
      <PageHeader
        breadcrumb={[
          { label: t('setup.title'), to: canOpenSetupHome ? pathForDestination('setup') : undefined },
          t('people.title'),
        ]}
        title={t('people.title')}
        summary={summary}
        action={action}
      />
      {items.length > 1 ? <AreaNav label={t('people.nav.label')} items={items} /> : null}
      <h2 className="sr-only">{pageTitle}</h2>
      {children}
    </div>
  )
}

export function RolePills({ roles, label }: { roles: Role[]; label?: string }) {
  const t = useT()
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={label}>
      {roles.map((role) => (
        <li key={role.id}>
          <StatusPill tone="neutral">{roleLabel(t, role)}</StatusPill>
        </li>
      ))}
    </ul>
  )
}

/** One role in a role group: a checkbox, or — when it can't be changed — its name and the reason as text. */
export type RoleChoice = {
  role: RoleRow
  /** Why the role can't be changed here; the role is then shown as text, never as a disabled checkbox. */
  locked?: string
  description?: string
}

/*
 * The roles of a person (Invite, Change roles): built-in roles first, then
 * custom roles, each with its tick or its lock reason in words.
 */
export function RoleChoices({
  id,
  legend,
  help,
  error,
  choices,
  selected,
  onToggle,
}: {
  id: string
  legend: string
  help: string
  error?: string
  choices: RoleChoice[]
  selected: string[]
  onToggle: (roleId: string, next: boolean) => void
}) {
  const t = useT()
  const group = (kind: RoleRow['kind']) => choices.filter((choice) => choice.role.kind === kind)
  const builtIn = group('builtIn')
  const custom = group('custom')
  return (
    <FieldGroup id={id} legend={legend} help={help} error={error} required>
      {[
        { key: 'builtIn', heading: t('people.roles.builtIn'), items: builtIn },
        { key: 'custom', heading: t('people.roles.custom'), items: custom },
      ]
        .filter((section) => section.items.length > 0)
        .map((section) => (
          <div key={section.key} className="mt-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{section.heading}</p>
            <ul className="mt-1 flex flex-col">
              {section.items.map((choice) => (
                <ChoiceItem
                  key={choice.role.id}
                  id={`${id}-${choice.role.id}`}
                  label={roleLabel(t, choice.role)}
                  description={choice.description}
                  locked={choice.locked}
                  checked={selected.includes(choice.role.id)}
                  onChange={(next) => onToggle(choice.role.id, next)}
                />
              ))}
            </ul>
          </div>
        ))}
    </FieldGroup>
  )
}

/** A checkbox row with a label and optional description, or a locked row with the reason as text. */
export function ChoiceItem({
  id,
  label,
  description,
  locked,
  pill,
  checked,
  onChange,
}: {
  id: string
  label: string
  description?: string
  locked?: string
  pill?: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  if (locked) {
    return (
      <li className="flex flex-col gap-0.5 py-2 pl-11">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg-strong wrap-anywhere">
          {label}
          {pill ? <StatusPill tone="neutral">{pill}</StatusPill> : null}
        </span>
        <span className="text-xs text-fg-muted">{locked}</span>
      </li>
    )
  }
  const descriptionId = description ? `${id}-description` : undefined
  return (
    <li className="flex items-start">
      <Checkbox
        id={id}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-describedby={descriptionId}
      />
      <div className="flex min-w-0 flex-col gap-0.5 py-2.5">
        <label htmlFor={id} className="text-sm font-medium text-fg-strong wrap-anywhere">
          {label}
        </label>
        {description ? (
          <span id={descriptionId} className="text-xs text-fg-muted wrap-anywhere">
            {description}
          </span>
        ) : null}
      </div>
    </li>
  )
}
